import { useMemo } from 'react'
import { Box } from '@mui/material'
import { BaseTable } from 'Features/Table/BaseTable.view'
import { ExtendedGridColDef } from 'Features/Table/BulkUpdateModal'
import {
  SheduleTableProps,
  SchedulerTankWeekStateRowType,
} from './Scheduler.types'

/**
 * Per-tank opening stock and closing target for ONE week —
 * `TankWeekStateDelta`, stored under `Week{N}/`.
 *
 * Only `tankId` is required: a tank that neither opens with stock nor has a
 * target is a legitimate row, and blanks read as 0 rather than as an error.
 */
const dataConfig = {
  required: ['tankId'],
  optional: [
    'productCode',
    'openingQuantity',
    'hoursInTankAtStart',
    'closingTargetQuantity',
  ],
}

export const SchedulerTankWeekStateTable = ({
  data,
  isLoading,
  updateTableData,
  uploadScheduleData,
  isUploading,
  isSaving,
  updateRowsStatus,
  predicate,
  deleteTableData,
  isDeleting,
}: SheduleTableProps<SchedulerTankWeekStateRowType>) => {
  const handleDataUpdate = (
    changedRows: Record<number, SchedulerTankWeekStateRowType>
  ) => {
    updateTableData(changedRows)
  }

  const columns: ExtendedGridColDef[] = useMemo(
    () => [
      {
        headerName: 'Tank',
        field: 'tankId',
        type: 'string',
        flex: 1,
        editable: true,
      },
      {
        headerName: 'Product Code',
        field: 'productCode',
        type: 'string',
        flex: 1,
        editable: true,
      },
      {
        headerName: 'Opening Quantity',
        field: 'openingQuantity',
        type: 'number',
        flex: 1,
        editable: true,
        description:
          'How much is in the vessel at hour 0 of this week — last week’s closing quantity.',
      },
      {
        headerName: 'Hours In Tank At Start',
        field: 'hoursInTankAtStart',
        type: 'number',
        flex: 1,
        editable: true,
        description:
          'How long the opening stock has already been held, in HOURS. Blank assumes none of the maturation has elapsed, which can only delay a drain, never allow an early one.',
      },
      {
        headerName: 'Closing Target Quantity',
        field: 'closingTargetQuantity',
        type: 'number',
        flex: 1,
        editable: true,
        description:
          'How much should be in the vessel at the end of this week. Becomes demand on the product the tank’s fill segment makes.',
      },
    ],
    []
  )

  return (
    <Box>
      <BaseTable
        data={data ?? []}
        columns={columns}
        isLoading={isLoading}
        updateTableData={handleDataUpdate}
        uploadData={uploadScheduleData}
        isUploading={isUploading}
        isSaving={isSaving}
        checkBoxSelection={true}
        showTableToolBar={true}
        downloadFileName="SchedulerTankWeekState"
        onDeleteRows={deleteTableData}
        isDeleting={isDeleting}
        multiStageUpload={{
          uploadFile: uploadScheduleData,
          isUploading: isUploading,
          columns: columns,
          updateRowsStatus: updateRowsStatus,
          originalData: data,
          predicate: predicate,
          dataConfig: dataConfig,
        }}
        initialState={{
          pagination: { paginationModel: { pageSize: 100 } },
        }}
        sx={{
          maxWidth: 'calc(100vw - 70px)',
          '& .MuiDataGrid-cell--editable': {
            backgroundColor: '#CBD5E1',
          },
          '& .MuiBox-root': {
            padding: '0px !important',
          },
        }}
      />
    </Box>
  )
}
